import Skeleton, { SkeletonText } from "@/components/Skeleton";

/**
 * In-page placeholder for `UserGrid` - the teacher cards on the teachers
 * listing. Matches the real 1/2/3-column grid and `UserCard`'s shape: a tall
 * rounded portrait, a name, a country line and a full-width profile button.
 */
export function TeachersGridSkeleton({ cards = 8 }: { cards?: number }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className="rounded-2xl bg-white p-5 shadow-md">
          <Skeleton className="mb-3 h-65 w-full rounded-xl" />
          <Skeleton className="mb-2 h-6 w-3/4" />
          <div className="mb-3 flex items-center gap-2">
            <Skeleton shape="circle" className="h-4 w-4" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * In-page placeholder for the filter row on the teachers listing, shown while
 * the country/language metadata loads.
 */
export function TeacherFiltersSkeleton() {
  return (
    <div className="flex flex-wrap gap-3 py-8">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-11 flex-1 min-w-[160px]" />
      ))}
      <Skeleton className="h-11 w-14" />
    </div>
  );
}

/**
 * In-page placeholder for a single teacher's profile page - portrait and
 * summary alongside the biography and detail blocks.
 */
export function TeacherDetailSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-12">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[320px_1fr]">
        {/* Portrait + summary */}
        <div className="space-y-4">
          <Skeleton className="h-80 w-full rounded-2xl" />
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-11 w-full" />
        </div>

        {/* Biography + details */}
        <div className="space-y-8">
          <div className="space-y-3">
            <Skeleton className="h-7 w-48" />
            <SkeletonText lines={5} />
          </div>

          <div className="space-y-3">
            <Skeleton className="h-6 w-40" />
            <div className="flex flex-wrap gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} shape="pill" className="h-8 w-24" />
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Skeleton className="h-6 w-44" />
            <SkeletonText lines={3} />
          </div>
        </div>
      </div>
    </div>
  );
}
